/**
 * Tiny localStorage cache helpers used across the app to make repeat visits
 * feel instant.  We hydrate the UI from cache first, then refresh in the
 * background — so users never stare at a spinner if we have any data
 * remotely recent enough to be useful.
 *
 * Usage:
 *   import { loadCache, saveCache } from "@/lib/localCache";
 *   const cached = loadCache("weight_summary_v1", 60 * 60 * 1000);
 *   if (cached) setState(cached);
 *   const fresh = await api.get(...);
 *   setState(fresh);
 *   saveCache("weight_summary_v1", fresh);
 */

export function loadCache(key, ttlMs) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (Date.now() - (parsed.savedAt || 0) > ttlMs) return null;
    return parsed.value;
  } catch {
    return null;
  }
}

export function saveCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify({ value, savedAt: Date.now() }));
  } catch {
    /* quota exceeded / private mode — ignore */
  }
}

export function clearCache(key) {
  try {
    localStorage.removeItem(key);
  } catch { /* noop */ }
}
